import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PolarEmbedCheckout } from '@polar-sh/checkout/embed';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import { ff, mono } from '../styles/fonts';
import { usePageMeta } from '../hooks/usePageMeta';
import { useSchema } from '../hooks/useSchema';
import { useAuth } from '../context/AuthContext';

// ─── Tier definition ──────────────────────────────────────────────────────────
export interface Tier {
  name: 'Hiker' | 'Explorer' | 'Master';
  icon: string;
  description: string;
  blurb: string;
  features: string[];
  highlighted: boolean;
  badge?: string;
  planKey: 'free' | 'plus' | 'pro';
  accentHex: string;
}

const TIERS: Tier[] = [
  {
    name: 'Hiker',
    icon: 'directions_walk',
    planKey: 'free',
    accentHex: '#8B9467',
    description: 'Free forever',
    blurb: 'Essential active learning. Perfect for mastering a single challenging university subject with flashcards and quizzes.',
    features: [
      '1 Active Subject',
      '1 Document per Subject',
      'Full-Subject Notes Synthesized from Uploads',
      'Unlimited Adaptive Quizzes with Mastery Tracking',
      'Spaced Repetition Flashcards',
      'AI Audio Lessons (Narration)',
      'Community Support',
    ],
    highlighted: false,
  },
  {
    name: 'Explorer',
    icon: 'explore',
    planKey: 'plus',
    accentHex: '#D46B4E',
    description: 'Full academic toolkit',
    blurb: 'Dive deep into any course with interactive Socratic tutoring, problem labs, and unlimited subjects.',
    features: [
      'Unlimited Subjects',
      'Up to 5 Documents per Subject',
      'Socratic AI Tutoring ("Teach Me Until I Get It")',
      'Full Animated AI Video Lessons',
      'Access to All Learnex Problem Labs',
      'Custom Visualizations & Mind Maps',
      'Priority Generation Speed',
      'Priority Email Support',
    ],
    highlighted: true,
    badge: 'MOST POPULAR ROUTE',
  },
  {
    name: 'Master',
    icon: 'account_balance',
    planKey: 'pro',
    accentHex: '#407B65',
    description: 'Priority everything',
    blurb: 'For serious pre-med, STEM, and honors students needing maximum exam pressure testing and 1-on-1 support.',
    features: [
      'Everything in Explorer',
      'Up to 15 Documents per Subject',
      'Unlimited Socratic Tutoring Sessions',
      'Timed Exam Simulator (Full Mock Exams)',
      'Transparent Exam Readiness Score',
      'Priority Video & Audio Rendering',
      'Early Access to New Experimental Labs',
      'Direct 1-on-1 Developer Support',
    ],
    highlighted: false,
  },
];

// ─── Comparison table ─────────────────────────────────────────────────────────
type ComparisonRow = {
  feature: string;
  free: string | boolean;
  plus: string | boolean;
  pro: string | boolean;
};

const comparisonRows: ComparisonRow[] = [
  { feature: 'Active Subjects',              free: '1',                 plus: 'Unlimited',         pro: 'Unlimited' },
  { feature: 'Documents per Subject',        free: '1',                 plus: '5',                 pro: '15' },
  { feature: 'Full-Subject Notes Synthesis', free: true,                plus: true,                pro: true },
  { feature: 'Spaced Repetition Flashcards', free: true,                plus: true,                pro: true },
  { feature: 'Unlimited Adaptive Quizzes',   free: true,                plus: true,                pro: true },
  { feature: 'AI Audio Lessons',             free: true,                plus: true,                pro: true },
  { feature: 'Full Animated Video Lessons',  free: false,               plus: true,                pro: true },
  { feature: 'Socratic AI Tutoring',         free: false,               plus: true,                pro: true },
  { feature: 'Learnex Problem Labs',         free: false,               plus: true,                pro: true },
  { feature: 'Timed Exam Simulator',         free: false,               plus: false,               pro: true },
  { feature: 'Exam Readiness Score',         free: false,               plus: false,               pro: true },
  { feature: 'Generation Speed',             free: 'Standard',          plus: 'Fast',              pro: 'Instant Priority' },
  { feature: 'Support Level',                free: 'Community',         plus: 'Priority Support',  pro: '1-on-1 Developer' },
  { feature: 'Student Discount (.edu)',      free: 'Free Always',       plus: '20% Off ($3.99/mo)', pro: '20% Off ($6.39/mo)' },
];

function CellValue({ value, isExplorer }: { value: string | boolean; isExplorer?: boolean }) {
  if (value === true) {
    return (
      <span className={`material-symbols-outlined text-[18px] ${isExplorer ? 'text-primary-container' : 'text-on-surface'}`} aria-hidden="true">
        check
      </span>
    );
  }
  if (value === false) {
    return <span className="text-on-surface-variant/30 text-lg">—</span>;
  }
  return <span className={isExplorer ? 'text-primary-container font-semibold' : ''}>{value as string}</span>;
}

// ─── Price fetcher hook ───────────────────────────────────────────────────────
type PriceData = { display: string; amount: number; currency: string } | null;
type PricesResponse = { explorer: PriceData; master: PriceData };

function usePolarPrices(apiUrl: string) {
  const [prices, setPrices]   = useState<PricesResponse>({ explorer: null, master: null });
  const [loading, setLoading] = useState(true);
  const fetchedRef            = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    fetch(`${apiUrl}/api/billing/prices`)
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        if (json) setPrices(json);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [apiUrl]);

  return { prices, loading };
}

// ─── Pricing FAQs ─────────────────────────────────────────────────────────────
const pricingFaqs = [
  {
    q: 'What is included in the free Hiker plan?',
    a: 'The Hiker plan is 100% free forever and includes 1 active subject, document processing, unlimited adaptive quizzes with mastery tracking, spaced repetition flashcards, full-subject notes synthesis, and AI audio lessons. You do not need a credit card to get started.',
  },
  {
    q: 'How does the 20% student discount work?',
    a: 'Students with a valid .edu email address automatically qualify for a 20% discount on both the Explorer ($3.99/month instead of $4.99) and Master ($6.39/month instead of $7.99) plans. The discount applies automatically when you sign up with your institutional email.',
  },
  {
    q: 'Can I cancel or switch plans at any time?',
    a: 'Yes. There are no contracts or commitments. You can upgrade, downgrade, or cancel your subscription at any time with a single click from Settings → Plan & Billing. You will retain access until the end of your current billing period.',
  },
  {
    q: 'How is payment handled and is my card information secure?',
    a: 'All payments and billing subscriptions are securely processed by Polar as our Merchant of Record. Learnex never stores or sees your raw credit card number or CVV.',
  },
  {
    q: 'What is the difference between Explorer and Master?',
    a: 'Explorer ($4.99/mo) unlocks unlimited subjects, Socratic AI tutoring, full animated video lessons, and all Learnex Problem Labs. Master ($7.99/mo) adds the Timed Exam Simulator, transparent Exam Readiness Score, priority rendering, and 1-on-1 support.',
  },
  {
    q: 'Does Learnex offer refunds?',
    a: 'Yes, we offer a hassle-free refund within 7 days of purchase if you are unsatisfied with our paid features. Contact support@learnex.cc and our team will process it promptly.',
  },
];

// ─── Main component ───────────────────────────────────────────────────────────
type CheckoutTierId = 'plus' | 'pro';
type CheckoutState  = 'idle' | 'loading' | 'overlay_open' | 'error';

export function PricingContent({ showCompare = true, isLandingPage = false }: { showCompare?: boolean; isLandingPage?: boolean }) {
  const {
    session,
    plan: currentPlan,
    loading: authLoading,
    refreshPlan,
    earlyAccess,
    earlyAccessDiscountPercent,
    earlyAccessChoice,
    earlyAccessWindowOpen,
  } = useAuth();
  const navigate = useNavigate();

  const apiUrl = import.meta.env.VITE_API_URL ?? '';
  const { prices, loading: pricesLoading } = usePolarPrices(apiUrl);

  const [checkoutState, setCheckoutState] = useState<Record<CheckoutTierId, CheckoutState>>({
    plus: 'idle',
    pro:  'idle',
  });
  const [errorMsg, setErrorMsg]                       = useState('');
  const [claimingEarlyAccess, setClaimingEarlyAccess] = useState(false);
  const [choiceLoading, setChoiceLoading]             = useState<'paid_discount' | 'free' | null>(null);
  const [publicWindowOpen, setPublicWindowOpen]       = useState(false);
  const activeCheckoutRef                             = useRef<PolarEmbedCheckout | null>(null);

  useEffect(() => {
    fetch(`${apiUrl}/api/billing/early-access-status`)
      .then(r => (r.ok ? r.json() : null))
      .then(json => setPublicWindowOpen(json?.window_open === true))
      .catch(() => setPublicWindowOpen(false));
  }, [apiUrl]);

  const windowOpen = publicWindowOpen || earlyAccessWindowOpen;
  const needsEarlyAccessChoice = !!session && earlyAccess && !earlyAccessChoice && !windowOpen;

  // ── Claim free early access ─────────────────────────────────────────────────
  const claimEarlyAccess = async () => {
    if (!session) { navigate('/login', { state: { next: '/pricing' } }); return; }
    setClaimingEarlyAccess(true);
    setErrorMsg('');
    try {
      const res  = await fetch(`${apiUrl}/api/billing/claim-early-access`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const json = await res.json();
      if (!res.ok) { setErrorMsg(json?.error ?? 'Could not activate early access. Please try again.'); return; }
      await refreshPlan();
      navigate('/welcome');
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
    } finally {
      setClaimingEarlyAccess(false);
    }
  };

  // ── Resolve early-access choice ─────────────────────────────────────────────
  const resolveEarlyAccessChoice = async (choice: 'paid_discount' | 'free') => {
    if (!session) return;
    setChoiceLoading(choice);
    setErrorMsg('');
    try {
      const res  = await fetch(`${apiUrl}/api/billing/early-access-choice`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ choice }),
      });
      const json = await res.json();
      if (!res.ok) { setErrorMsg(json?.error ?? 'Could not save your choice. Please try again.'); return; }
      await refreshPlan();
      if (choice === 'paid_discount') {
        const explorerTier = TIERS.find(t => t.planKey === 'plus');
        if (explorerTier) await handleSubscribe(explorerTier);
      }
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
    } finally {
      setChoiceLoading(null);
    }
  };

  // ── Subscribe / checkout ────────────────────────────────────────────────────
  const handleSubscribe = async (tier: Tier) => {
    // Free tier → sign up
    if (tier.planKey === 'free') { navigate('/signup'); return; }

    // Not signed in → login
    if (!session) { navigate('/login', { state: { next: '/pricing' } }); return; }

    const planKey = tier.planKey as CheckoutTierId;

    // Already on this plan → go to dashboard
    if (currentPlan === planKey) { navigate('/app/dashboard'); return; }

    // Early access window still open + Explorer → claim free
    if (planKey === 'plus' && windowOpen && !earlyAccess) {
      await claimEarlyAccess();
      return;
    }

    setCheckoutState(prev => ({ ...prev, [planKey]: 'loading' }));
    setErrorMsg('');

    try {
      const res  = await fetch(`${apiUrl}/api/billing/checkout`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ plan: planKey }),
      });
      const json = await res.json();

      if (!res.ok) {
        setErrorMsg(json?.error ?? 'Something went wrong. Please try again.');
        setCheckoutState(prev => ({ ...prev, [planKey]: 'error' }));
        return;
      }

      const checkoutUrl: string = json.checkout_url;
      if (!checkoutUrl) {
        setErrorMsg('No checkout URL returned. Please try again.');
        setCheckoutState(prev => ({ ...prev, [planKey]: 'error' }));
        return;
      }

      // Open Polar embedded checkout iframe
      if (activeCheckoutRef.current) {
        try { activeCheckoutRef.current.close(); } catch {}
      }

      const polar = await PolarEmbedCheckout.create(checkoutUrl, {
        theme: 'light',
      });
      activeCheckoutRef.current = polar;

      setCheckoutState(prev => ({ ...prev, [planKey]: 'overlay_open' }));

      polar.addEventListener('close', async () => {
        setCheckoutState(prev => ({ ...prev, [planKey]: 'loading' }));
        await refreshPlan();
        setCheckoutState(prev => ({
          ...prev,
          [planKey]: prev[planKey] === 'overlay_open' ? 'idle' : prev[planKey],
        }));
        activeCheckoutRef.current = null;
      });

    } catch (err) {
      console.error('[PricingPage] Checkout error:', err);
      setErrorMsg('Network error. Please check your connection and try again.');
      setCheckoutState(prev => ({ ...prev, [planKey]: 'error' }));
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const getCtaLabel = (tier: Tier): string => {
    if (tier.planKey === 'free') return 'START FREE';
    const planKey = tier.planKey as CheckoutTierId;
    if (!authLoading && session && currentPlan === planKey) return 'CURRENT PLAN';
    if (planKey === 'plus' && windowOpen && !earlyAccess) {
      return claimingEarlyAccess ? 'Activating…' : 'CLAIM FREE ACCESS';
    }
    const state = checkoutState[planKey];
    if (state === 'loading')      return 'Opening checkout…';
    if (state === 'overlay_open') return 'Complete in overlay…';
    if (tier.planKey === 'plus') return 'GET EXPLORER';
    return 'GET MASTER';
  };

  const getFormattedPrice = (tier: Tier): string => {
    if (tier.planKey === 'free') return '';
    if (pricesLoading) {
      return tier.planKey === 'plus' ? '$4.99' : '$7.99';
    }
    const priceData = tier.planKey === 'plus' ? prices.explorer : prices.master;
    return priceData?.display ?? (tier.planKey === 'plus' ? '$4.99' : '$7.99');
  };

  const isCurrent = (tier: Tier) =>
    !!session && tier.planKey !== 'free' && currentPlan === tier.planKey;

  return (
    <>
      {/* ── Header ── */}
      <header className={`pb-12 text-center ${isLandingPage ? 'pt-0' : 'pt-20'}`}>
        <div className="max-w-[800px] mx-auto px-base md:px-gutter space-y-4">
          <p className="text-label-caps text-ink-muted" style={mono}>
            PLANS &amp; PRICING
          </p>
          {isLandingPage ? (
            <h2 className="text-4xl md:text-5xl font-bold text-on-surface" style={ff}>
              Transparent, Student-Friendly Pricing
            </h2>
          ) : (
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-on-surface leading-tight" style={ff}>
              Transparent, Student-Friendly Pricing
            </h1>
          )}
          <p className="text-lg text-on-surface-variant max-w-2xl mx-auto leading-relaxed">
            Start free forever with our Hiker plan. Upgrade to Explorer ($4.99/mo) or Master ($7.99/mo) for
            Socratic AI tutoring, problem labs, and timed mock exams. Verified .edu students get 20% off.
          </p>
        </div>
      </header>

      {/* ── Early access window banner ── */}
      {windowOpen && !earlyAccess && (
        <div className="max-w-[1000px] mx-auto px-base md:px-gutter w-full mb-6">
          <div className="p-4 rounded-xl bg-[#D46B4E]/10 border border-[#D46B4E]/30 flex items-start gap-3">
            <span className="material-symbols-outlined text-[#D46B4E] text-[18px] mt-0.5" aria-hidden="true">rocket_launch</span>
            <p className="text-sm text-on-surface">
              <strong>Launch Special!</strong> For the first 15 days, sign up to get our Explorer plan completely free. Plus, if you upgrade to any paid plan during this period, you&apos;ll lock in a massive 50% discount for life!
            </p>
          </div>
        </div>
      )}

      {/* ── Resolve early access (billing live, choice not made yet) ── */}
      {needsEarlyAccessChoice && (
        <div className="max-w-[1000px] mx-auto px-base md:px-gutter w-full mb-6">
          <div className="p-5 rounded-xl bg-[#286958]/5 border border-[#286958]/25 flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div>
              <p className="text-sm font-semibold text-on-surface">Payments are live — keep your Explorer access?</p>
              <p className="text-sm text-on-surface-variant mt-1">
                As an early access member you get {earlyAccessDiscountPercent ?? 50}% off Explorer for life,
                or you can switch to the free Hiker plan instead.
              </p>
            </div>
            <div className="flex gap-3 shrink-0">
              <button
                onClick={() => resolveEarlyAccessChoice('free')}
                disabled={choiceLoading !== null}
                className="px-4 py-2 rounded-full text-sm font-semibold border border-outline-variant/40 text-on-surface-variant hover:border-outline-variant/70 disabled:opacity-50"
              >
                Use Free Hiker Plan
              </button>
              <button
                onClick={() => resolveEarlyAccessChoice('paid_discount')}
                disabled={choiceLoading !== null}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-container text-white hover:opacity-90 disabled:opacity-50"
              >
                Keep Explorer with Discount
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Error message ── */}
      {errorMsg && (
        <div className="max-w-[1000px] mx-auto px-base md:px-gutter w-full mb-4">
          <div className="p-4 rounded-xl bg-error/5 border border-error/20 flex items-start gap-3">
            <span className="material-symbols-outlined text-error text-[18px] mt-0.5" aria-hidden="true">error</span>
            <p className="text-sm text-error">{errorMsg}</p>
          </div>
        </div>
      )}

      {/* ── Plan cards ── */}
      <section aria-label="Pricing plans" className="pb-16">
        <div className="max-w-[1050px] mx-auto px-base md:px-gutter grid md:grid-cols-3 gap-6 items-stretch">
          {TIERS.map((tier) => {
            const label      = getCtaLabel(tier);
            const isCur      = isCurrent(tier);
            const planKey    = tier.planKey as CheckoutTierId;
            const state      = tier.planKey !== 'free' ? checkoutState[planKey] : 'idle';
            const isDisabled = isCur || state === 'loading' || state === 'overlay_open' || claimingEarlyAccess;
            const price      = getFormattedPrice(tier);

            return (
              <div
                key={tier.planKey}
                className={`relative bg-surface-white rounded-3xl p-8 flex flex-col transition-all ${
                  tier.highlighted
                    ? 'border-2 border-primary-container shadow-lg ring-4 ring-primary-container/10'
                    : 'border border-outline-variant/20 shadow-sm'
                }`}
              >
                {/* Badge */}
                {tier.badge && (
                  <div
                    className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary-container text-white text-[10px] font-bold px-4 py-1.5 rounded-full tracking-widest whitespace-nowrap"
                    style={mono}
                  >
                    {tier.badge}
                  </div>
                )}

                {/* Icon + Descriptive Heading */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tier.highlighted ? 'bg-primary-container/10' : 'bg-surface-container-low'}`}>
                    <span className={`material-symbols-outlined text-[20px] ${tier.highlighted ? 'text-primary-container' : 'text-on-surface-variant'}`} aria-hidden="true">
                      {tier.icon}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-on-surface" style={ff}>
                    {tier.name === 'Hiker' ? 'Hiker (Free Plan)' : `${tier.name} Plan`}
                  </h2>
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-3 min-h-[3.5rem]">
                  {tier.planKey === 'free' ? (
                    <span className="text-5xl font-bold text-on-surface" style={ff}>Free</span>
                  ) : price ? (
                    <>
                      <span className="text-4xl font-bold text-on-surface" style={ff}>{price}</span>
                      <span className="text-sm text-on-surface-variant ml-1">/ month</span>
                    </>
                  ) : (
                    <span className="text-lg font-semibold text-on-surface-variant" style={ff}>See checkout</span>
                  )}
                </div>

                {/* Blurb */}
                <p className="text-sm text-on-surface-variant mb-6 leading-relaxed min-h-[2.5rem]">{tier.blurb}</p>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-on-surface">
                      <span className="material-symbols-outlined text-[18px] text-primary-container shrink-0 mt-0.5" aria-hidden="true">check_circle</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA with real accessible link fallback */}
                {!session && tier.planKey === 'free' ? (
                  <Link
                    to="/signup"
                    className="block w-full text-center py-3.5 rounded-xl text-sm font-semibold tracking-widest transition-all border border-outline-variant/40 text-on-surface hover:bg-surface-container-low shadow-sm"
                    style={mono}
                  >
                    {label}
                  </Link>
                ) : !session ? (
                  <Link
                    to="/login"
                    state={{ next: '/pricing' }}
                    className={`block w-full text-center py-3.5 rounded-xl text-sm font-semibold tracking-widest transition-all shadow-sm ${
                      tier.highlighted
                        ? 'bg-primary-container text-white hover:opacity-90 shadow-md'
                        : 'border border-outline-variant/40 text-on-surface hover:bg-surface-container-low'
                    }`}
                    style={mono}
                  >
                    {label}
                  </Link>
                ) : (
                  <button
                    onClick={() => handleSubscribe(tier)}
                    disabled={isDisabled}
                    className={`block w-full text-center py-3.5 rounded-xl text-sm font-semibold tracking-widest transition-all disabled:opacity-60 disabled:cursor-default flex items-center justify-center gap-2 ${
                      isCur
                        ? 'bg-secondary-container text-on-secondary-container'
                        : tier.highlighted
                        ? 'bg-primary-container text-white hover:opacity-90 shadow-md'
                        : 'border border-outline-variant/40 text-on-surface hover:bg-surface-container-low'
                    }`}
                    style={mono}
                  >
                    {state === 'loading' && (
                      <div className="w-4 h-4 rounded-full border-2 border-current/30 border-t-current animate-spin" />
                    )}
                    {label}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Student Discount Banner ── */}
      <section className="max-w-[1050px] mx-auto px-base md:px-gutter mb-20 w-full">
        <div className="bg-primary/5 border border-primary/20 rounded-3xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="space-y-2 text-center md:text-left">
            <div className="inline-flex items-center gap-2 text-xs font-bold text-primary tracking-widest uppercase" style={mono}>
              <span className="material-symbols-outlined text-[16px]" aria-hidden="true">school</span>
              EDUCATION PROGRAM
            </div>
            <h2 className="text-2xl font-bold text-on-surface" style={ff}>
              20% Student Discount with Any .edu Email
            </h2>
            <p className="text-sm text-on-surface-variant max-w-xl">
              We know university budgets are tight. All enrolled students with a verified .edu email address automatically receive 20% off Explorer ($3.99/mo) and Master ($6.39/mo) plans.
            </p>
          </div>
          <Link
            to="/signup"
            className="shrink-0 px-6 py-3 rounded-full bg-primary text-on-primary font-semibold text-sm hover:opacity-90 transition shadow-sm"
          >
            Claim Student Discount &rarr;
          </Link>
        </div>
      </section>

      {/* ── Compare the Paths ── */}
      {showCompare && (
        <section aria-labelledby="compare-heading" className="pb-24">
          <div className="max-w-[950px] mx-auto px-base md:px-gutter">
            <div className="text-center mb-10">
              <p className="text-label-caps text-ink-muted mb-2" style={mono}>FEATURE MATRIX</p>
              <h2 id="compare-heading" className="text-3xl md:text-4xl font-bold text-on-surface" style={ff}>
                Compare Learnex Plans Side by Side
              </h2>
            </div>
            <div className="bg-surface-white rounded-3xl border border-outline-variant/20 shadow-sm overflow-hidden">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-outline-variant/15 bg-surface">
                    <th className="py-4 px-6 text-xs font-bold text-on-surface-variant tracking-widest" style={mono}>FEATURE</th>
                    <th className="py-4 px-4 text-sm font-semibold text-on-surface text-center">Hiker (Free)</th>
                    <th className="py-4 px-4 text-sm font-semibold text-primary-container text-center bg-primary-container/5">Explorer ($4.99)</th>
                    <th className="py-4 px-4 text-sm font-semibold text-on-surface text-center">Master ($7.99)</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row) => (
                    <tr key={row.feature} className="border-b border-outline-variant/10 last:border-0 hover:bg-surface-container-lowest transition-colors">
                      <td className="py-4 px-6 text-sm text-on-surface font-medium">{row.feature}</td>
                      <td className="py-4 px-4 text-sm text-on-surface-variant text-center"><CellValue value={row.free} /></td>
                      <td className="py-4 px-4 text-sm text-center bg-primary-container/5"><CellValue value={row.plus} isExplorer /></td>
                      <td className="py-4 px-4 text-sm text-on-surface-variant text-center"><CellValue value={row.pro} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* ── Pricing FAQs ── */}
      {showCompare && (
        <section aria-labelledby="pricing-faq-heading" className="pb-28 border-t border-outline-variant/10 pt-20">
          <div className="max-w-[800px] mx-auto px-base md:px-gutter">
            <div className="text-center mb-12">
              <p className="text-label-caps text-ink-muted mb-2" style={mono}>QUESTIONS &amp; ANSWERS</p>
              <h2 id="pricing-faq-heading" className="text-3xl md:text-4xl font-bold text-on-surface" style={ff}>
                Frequently Asked Questions About Pricing
              </h2>
            </div>

            <div className="space-y-4">
              {pricingFaqs.map((faq, i) => (
                <details key={i} className="border border-outline-variant/20 rounded-2xl px-6 py-4 bg-surface-white shadow-sm group">
                  <summary className="font-semibold text-on-surface cursor-pointer text-base md:text-lg list-none flex items-center justify-between">
                    <h3 className="inline text-left">{faq.q}</h3>
                    <svg className="w-5 h-5 text-ink-muted shrink-0 ml-4 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="mt-3 text-sm md:text-base text-on-surface-variant leading-relaxed">
                    {faq.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

export default function PricingPage() {
  usePageMeta(
    'Learnex Pricing — Free Hiker, Explorer ($4.99/mo) & Master ($7.99/mo) Plans',
    'Transparent, student-friendly study tool pricing: unlimited quizzes and flashcards free forever. Explorer ($4.99/mo) and Master ($7.99/mo) unlock Socratic tutoring and mock exams. 20% .edu discount.',
    '/pricing'
  );

  useSchema({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': 'https://learnex.cc/pricing#webpage',
        url: 'https://learnex.cc/pricing',
        name: 'Learnex Pricing & Plans',
        description: 'Simple, honest pricing for college students: Hiker free plan, Explorer ($4.99/mo), and Master ($7.99/mo) with 20% .edu discount.',
        dateModified: '2026-11-05',
        isPartOf: { '@id': 'https://learnex.cc/#website' },
      },
      {
        '@type': 'SoftwareApplication',
        name: 'Learnex',
        applicationCategory: 'EducationalApplication',
        operatingSystem: 'Web',
        url: 'https://learnex.cc/pricing',
        description: 'AI study platform that transforms course materials into personalized study paths, flashcards, quizzes, and Socratic tutoring.',
        offers: [
          {
            '@type': 'Offer',
            name: 'Hiker (Free Plan)',
            price: '0',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            description: '1 active subject, unlimited quizzes and flashcards, audio lessons, free forever.',
          },
          {
            '@type': 'Offer',
            name: 'Explorer Plan',
            price: '4.99',
            priceCurrency: 'USD',
            billingPeriod: 'P1M',
            availability: 'https://schema.org/InStock',
            description: 'Unlimited subjects, Socratic AI tutoring, full animated AI video lessons, and problem labs.',
          },
          {
            '@type': 'Offer',
            name: 'Master Plan',
            price: '7.99',
            priceCurrency: 'USD',
            billingPeriod: 'P1M',
            availability: 'https://schema.org/InStock',
            description: 'Everything in Explorer plus Timed Exam Simulator, transparent Exam Readiness Score, and 1-on-1 support.',
          },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: pricingFaqs.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.a,
          },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://learnex.cc/' },
          { '@type': 'ListItem', position: 2, name: 'Pricing', item: 'https://learnex.cc/pricing' },
        ],
      },
    ],
  });

  return (
    <div className="bg-paper-bg text-on-surface antialiased min-h-screen flex flex-col noise-bg">
      <PublicNav activePath="/pricing" />
      <PricingContent showCompare={true} />
      <PublicFooter />
    </div>
  );
}
